// src/components/store/MemberStoreProducts.jsx
// Component to display member stores with their products

import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { getStoresByType, STORE_TYPES } from '@/lib/storeManagement';
import { fetchStoreProductsPage } from '@/lib/storeProducts';
import ProductCard from '@/components/store/ProductCard';
import { useCart } from '@/contexts/CartContext';
import { ToastContext } from '@/components/ToastProvider';
import QuickViewModal from '@/components/store/QuickViewModal';
import { useNavigate } from 'react-router-dom';

export default function MemberStoreProducts({ userLoggedIn = false, selectedCategory = 'all' }) {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { notify } = React.useContext(ToastContext);

  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);

  // Quick view state
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [showQuickView, setShowQuickView] = useState(false);

  // Load member stores
  useEffect(() => {
    const fetchMemberStores = async () => {
      try {
        setLoading(true);
        const result = await getStoresByType(STORE_TYPES.MEMBER);

        if (result.success && result.stores.length > 0) {
          setStores(result.stores);
          setSelectedStoreId(result.stores[0].id);
          setSelectedStore(result.stores[0]);
        }
      } catch (err) {
        // Handle error silently
      } finally {
        setLoading(false);
      }
    };

    fetchMemberStores();
  }, []);

  // Update selected store details when the store selection changes
  useEffect(() => {
    if (!selectedStoreId) {
      setSelectedStore(null);
      return;
    }

    const store = stores.find((item) => item.id === selectedStoreId) || null;
    setSelectedStore(store);
  }, [selectedStoreId, stores]);

  // Load products and categories when store is selected
  useEffect(() => {
    if (!selectedStoreId) return;

    let unsubscribeCategories = () => {};
    let isCancelled = false;

    const loadStoreData = async () => {
      setProductsLoading(true);
      setProducts([]);
      setCategories([]);

      try {
        // Load products for the selected store
        const { items } = await fetchStoreProductsPage({
          storeType: STORE_TYPES.MEMBER,
          storeId: selectedStoreId,
          pageSize: 100
        });

        if (!isCancelled) {
          setProducts(items);
        }

        // Load categories for the selected store
        const categoriesQuery = query(
          collection(db, 'storeCategories'),
          where('storeId', '==', selectedStoreId)
        );

        unsubscribeCategories = onSnapshot(
          categoriesQuery,
          (snapshot) => {
            if (isCancelled) return;
            const categoriesData = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));

            setCategories(categoriesData);
          },
          () => {
            if (isCancelled) return;
            const defaultCategories = [
              {
                id: 'all-products',
                name: 'All Products',
                icon: '🛍️',
                storeId: selectedStoreId
              }
            ];
            setCategories(defaultCategories);
          }
        );
      } catch (error) {
        if (!isCancelled) {
          setProducts([]);
          setCategories([]);
        }
      } finally {
        if (!isCancelled) {
          setProductsLoading(false);
        }
      }
    };

    loadStoreData();

    return () => {
      isCancelled = true;
      unsubscribeCategories();
    };
  }, [selectedStoreId]);

  // Gracefully handle late-arriving stores
  useEffect(() => {
    if (selectedStoreId || stores.length === 0) return;
    setSelectedStoreId(stores[0].id);
  }, [selectedStoreId, stores]);

  const handleStoreSelect = (storeId) => {
    if (storeId === selectedStoreId) return;
    setSelectedStoreId(storeId);
  };

  const storeDisplayName = selectedStore?.name || selectedStore?.displayName || 'Member Store';

  // Filter products by category
  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' ||
      (product.categories && product.categories.includes(selectedCategory));

    return matchesCategory;
  });

  const handleAddToCart = (product) => {
    if (!userLoggedIn) {
      notify('Please sign in to add items to cart', 'info');
      return;
    }

    const qty = product.qty || product.quantityStep || 1;
    addItem({
      ...product,
      qty: qty,
      storeType: 'member',
      storeId: selectedStoreId
    });
    notify(`${product.name} added to cart!`, 'success');
  };

  const handleProductClick = (product) => {
    navigate(`/store/${product.id}`);
  };

  const handleQuickView = (product) => {
    setQuickViewProduct(product);
    setShowQuickView(true);
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Loading member stores...</p>
      </div>
    );
  }

  if (stores.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">🏪</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Member Stores Yet</h3>
        <p className="text-gray-600 mb-4">
          No cooperative members have created stores yet.
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
          <p className="text-blue-800 text-sm">
            Members can apply to create their own stores and sell products through our platform.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Member store selector */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">Member Stores</h2>
          <span className="text-sm text-gray-500">
            {stores.length} {stores.length === 1 ? 'store' : 'stores'} available
          </span>
        </div>
        <div
          className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1"
          role="tablist"
          aria-label="Member stores"
        >
          {stores.map((store) => (
            <button
              key={store.id}
              type="button"
              onClick={() => handleStoreSelect(store.id)}
              role="tab"
              aria-selected={selectedStoreId === store.id}
              className={`shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                selectedStoreId === store.id
                  ? 'bg-green-600 text-white border-green-600 shadow-sm'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-green-400 hover:text-green-700'
              }`}
            >
              {store.name || store.displayName || 'Member Store'}
            </button>
          ))}
        </div>
      </div>

      {selectedStore && (
        <>
          <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{storeDisplayName}</h3>
                {selectedStore?.location && (
                  <p className="text-sm text-gray-500 mt-1">
                    Based in {selectedStore.location}
                  </p>
                )}
                {selectedStore?.description && (
                  <p className="text-sm text-gray-600 mt-2">
                    {selectedStore.description}
                  </p>
                )}
              </div>
              {selectedStore?.logoUrl && (
                <img
                  src={selectedStore.logoUrl}
                  alt={`${storeDisplayName} logo`}
                  className="h-12 w-12 rounded-full object-cover border border-gray-200"
                />
              )}
            </div>
          </div>

          {/* Products Grid */}
          {productsLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <span className="ml-2 text-gray-600">Loading products...</span>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-2">🛍️</div>
              <p className="text-gray-600 text-sm">No products found</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {filteredProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  p={product}
                  onClick={() => handleProductClick(product)}
                  onAdd={(itemWithQty) => handleAddToCart(itemWithQty)}
                  showAdd={userLoggedIn}
                  showQuick={true}
                  onQuick={() => handleQuickView(product)}
                  userLoggedIn={userLoggedIn}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Quick View Modal */}
      <QuickViewModal
        product={quickViewProduct}
        isOpen={showQuickView}
        onClose={() => {
          setShowQuickView(false);
          setQuickViewProduct(null);
        }}
      />
    </div>
  );
}
